import { ComplianceReportDescription, ComplianceReportEnum, ExecutiveReportType, IcdCorporationStatusRM, Rotation } from '@herbjoy/apex-api-models';
import { IQService } from 'angular';
import moment from 'moment';
import { EventEmitter } from '../../common/components/event-emitter';
import { complianceReportOutputFormatList, ComplianceReportOutputFormatSelect } from '../../core/enum/compliance-report-output-format';
import { departmentList } from '../../core/enum/department';
import { DivisionService, EmployeeService, IcdCorporationService } from '../../core/services';
import { RotationsService, sortRotationDesc } from '../../core/services/data-services/rotations.service';
import { UtilityService } from '../../core/services/utility.service';
import templateUrl from './request-report.component.html';

export interface ReportJobRequest {
    startDate: Date;
    endDate: Date;
    reportType: ExecutiveReportType;
    OutputFormat: ComplianceReportOutputFormatSelect;
    reports: ComplianceReportEnum[];
    icdCorporations: number[];
    reportArguments: any;
}

export interface ReportJobVM {
    startDate: Date;
    endDate: Date;
    reportType: ExecutiveReportType;
    OutputFormat: ComplianceReportOutputFormatSelect;
    reports: ComplianceReportDescription[];
    icdCorporations: number[];
    reportArguments: any;
}

export const RequestReportComponent = {
    templateUrl,
    bindings: {
        reportDescriptions: "<",
        submitRequest: "&onSubmitRequest"
    },
    controller: class RequestReportComponent {
        // @Input
        reportDescriptions: ComplianceReportDescription[];
        submitRequest: EventEmitter<{ reportJob: ReportJobRequest }>;

        complianceReportOutputFormatList: ComplianceReportOutputFormatSelect[];
        reportJob: ReportJobVM;
        deparmentExclusionList: number[];
        statusExclusionList: number[];
        selectedCompensationProfiles: any[];
        icdCorporationStatuses: IcdCorporationStatusRM[];
        showOnlyActive = false;
        divisionProfiles: any;
        selectedDivisionProfiles: any[];

        selectedCorporationStatus: { Value: number, Name: string };
        selectedRotation: any;
        rotations: Rotation[];
        icdCorporations: { value: number, text: string, active: boolean }[];
        icdCorporationsBK: { value: number, text: string, active: boolean }[];
        compensationProfiles: { value: number, text: string }[];

        constructor(private $q: IQService, private divisionService: DivisionService,
            private icdCorporationService: IcdCorporationService, private rotationsService: RotationsService,
            private employeeService: EmployeeService, private utilityService: UtilityService) {
            'ngInject';
            this.complianceReportOutputFormatList = complianceReportOutputFormatList;
        }

        $onInit() {
            this.reportJob = {
                startDate: null,
                endDate: null,
                reportType: ExecutiveReportType.Compliance,
                OutputFormat: complianceReportOutputFormatList[1],//Select merged output report format as default
                reports: [], // Maintain a list of (flag) report types. These will be |'d together.
                icdCorporations: [],
                reportArguments: {}
            };
            this.deparmentExclusionList = [departmentList.dept750, departmentList.dept900];
            this.statusExclusionList = [3];
            this.selectedCompensationProfiles = [];

            const corporationsLoaded = this.loadIcdCorporations();

            const compensationProfilesLoaded = this.loadCompensationProfiles();

            const icdCorporationStatusesLoaded = this.icdCorporationService.retrieveIcdCorporationStatus()
                .then(statuses => this.icdCorporationStatuses = statuses
                    .filter(status => this.filterIcdCorporationStatuses(status)));

            const divisionsLoaded = this.divisionService.retrieveDivisionProfiles()
                .then(divisionProfiles => this.divisionProfiles = divisionProfiles.sort((a, b) => {
                    if (a.DivisionName < b.DivisionName) {
                        return -1;
                    } else if (a.DivisionName > b.DivisionName) {
                        return 1;
                    }
                    return a.Name < b.Name ? -1 : 1
                }).map(profile => ({
                    ...profile,
                    text: `${profile.DivisionName} (${profile.Name})`
                })));

            const rotationsLoaded = divisionsLoaded
                .then(() => this.loadRotations());

            this.utilityService.showLoadingUntilResolved(
                this.$q.all([corporationsLoaded, divisionsLoaded, rotationsLoaded,
                    compensationProfilesLoaded, icdCorporationStatusesLoaded])
            );
        }

        onShowOnlyActiveChange() {
            if (this.showOnlyActive) {
                this.icdCorporations = this.icdCorporationsBK.filter(c => c.active === true);
            } else {
                this.icdCorporations = this.icdCorporationsBK;
            }
        }

        onSubmit() {
            const reportArguments = this.reportJob.reportArguments || {};
            if (this.selectedDivisionProfiles) {
                reportArguments.DivisionProfileIDs = this.selectedDivisionProfiles.map(p => {
                    return p.DivisionProfileID
                });
                reportArguments.Division = this.selectedDivisionProfiles.map(p => {
                    return p.DivisionName
                });
            }

            if (this.selectedCorporationStatus) {
                reportArguments.CorporationStatusID = this.selectedCorporationStatus.Value;
            }

            if (this.selectedCompensationProfiles && this.selectedCompensationProfiles.length > 0) {
                reportArguments.CompensationProfilesIDs =
                    this.selectedCompensationProfiles.map(profile => profile.value);
            }

            this.submitRequest({
                $event: {
                    reportJob: {
                        ...this.reportJob,
                        reports: this.reportJob.reports.length
                            ? this.reportJob.reports.map(p => p.Id)
                            // When no reports are selected, pre-select all.
                            : this.reportDescriptions.map(p => p.Id),
                        reportArguments
                    }
                }
            });
        }

        onChangeDivision() {
            // Load payrolls for the specific division.
            const rotationsLoaded = this.loadRotations();
            const corporationsLoaded = this.loadIcdCorporations();
            this.utilityService.showLoadingUntilResolved(
                this.utilityService.alertOnError(this.$q.all([rotationsLoaded, corporationsLoaded]),
                    "Loading data failed")
            );
        }

        onChangeIcdCorporationStatus() {
            const corporationsLoaded = this.loadIcdCorporations();
            this.utilityService.showLoadingUntilResolved(
                this.utilityService.alertOnError(corporationsLoaded,
                    "Loading data failed")
            );
        }

        onChangeRotation() {
            if (this.selectedRotation) {
                this.reportJob.startDate = this.selectedRotation.StartDate;
                this.reportJob.endDate = this.selectedRotation.EndDate;
            }
        }

        loadRotations() {
            if (!this.selectedDivisionProfiles || !this.selectedDivisionProfiles.length) {
                this.rotations = [];
                return this.$q.resolve(this.rotations);
            }
            const divisionProfileIDs = this.selectedDivisionProfiles.map(p => {
                return p.DivisionProfileID
            });
            return this.rotationsService.retrieveDivisionRotations({
                divisionProfileID: divisionProfileIDs
            }).then(rotations => this.rotations = rotations
                .sort(sortRotationDesc)
                .map(rotation => ({
                    ...rotation,
                    text: `${rotation.PolicyName}(${rotation.DivisionName}) `
                        + `(${moment(rotation.StartDate).format("MMM DD, YYYY")} - ${moment(rotation.EndDate).format("MMM DD, YYYY")})`
                })));
        }

        loadIcdCorporations() {
            const query: any = {};
            if (this.selectedDivisionProfiles) {
                query.DivisionProfileIDs = this.selectedDivisionProfiles.map(p => {
                    return p.DivisionProfileID
                });
                query.IcdCorporationStatus = this.selectedCorporationStatus
                    ? this.selectedCorporationStatus.Name : null;
            }
            return this.icdCorporationService.retrieveManagedIcdCorporations(query)
                .then(icdCorporations => icdCorporations.map(icdCorporation => ({
                    value: icdCorporation.Id,
                    text: icdCorporation.Owner + ' - ' + icdCorporation.ClientName,
                    active: icdCorporation.IcdCorporationStatus === 'Active'
                })).sort((a, b) => {
                    // Sort by the new text field.
                    return a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1;
                }))
                .then(icdCorporations => {
                    this.icdCorporations = icdCorporations;
                    this.icdCorporationsBK = icdCorporations;
                });
        }

        loadCompensationProfiles() {
            return this.employeeService.retrieveCompensationProfiles()
                .then(compensationProfiles => compensationProfiles
                    .filter(compensationProfile =>
                        !this.deparmentExclusionList.includes(compensationProfile.Id))
                    .map(compensationProfile => ({
                        value: compensationProfile.Id,
                        text: compensationProfile.Name
                    })))
                .then(compensationProfiles => this.compensationProfiles = compensationProfiles);
        }

        filterIcdCorporationStatuses(status) {
            return !this.statusExclusionList.includes(status.Value)
        }
    }

}
